import React, { useEffect, useRef, useState } from "react";
import secondImage from "../../assets/images/icons/no_results.svg";
import zoomInIcon from "../../assets/images/icons/zoomIn.svg";
import zoomOutIcon from "../../assets/images/icons/zoomOut.svg";
import resetIcon from "../../assets/images/icons/reset.svg";
import rotateIcon from "../../assets/images/icons/rotate.svg";
import { useTranslation } from "react-i18next";
import { heicTo } from "heic-to";

interface IFilePreview {
  file?: string | null;
  type?: "IMAGE" | "PDF";
  isLoading?: boolean;
  url?: string;
}

const FilePreview: React.FC<IFilePreview> = ({
  file,
  type,
  isLoading,
  url,
}) => {
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [convertedSrc, setConvertedSrc] = useState<string | null>(null);
  const [isConverting, setIsConverting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const { t } = useTranslation();

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.2, 3));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.2, 0.5));
  };

  const handleRotateLeft = () => {
    setRotation((prev) => prev - 90);
  };

  const handleRotateRight = () => {
    setRotation((prev) => prev + 90);
  };

  const resetTransforms = () => {
    setZoom(1);
    setRotation(0);
    setPosition({ x: 0, y: 0 });
  };

  const handleWheelZoom = (event: React.WheelEvent<HTMLDivElement>) => {
    event.preventDefault();

    const zoomDelta = event.deltaY > 0 ? -0.1 : 0.1;

    const newZoom = Math.min(Math.max(zoom + zoomDelta, 0.5), 3);

    setZoom(newZoom);
  };

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (zoom > 1) {
      setIsDragging(true);

      setDragStart({
        x: event.clientX - position.x,
        y: event.clientY - position.y,
      });
    }
  };

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (isDragging && zoom > 1) {
      setPosition({
        x: event.clientX - dragStart.x,
        y: event.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    if (zoom > 1 && event.touches.length === 1) {
      setIsDragging(true);

      setDragStart({
        x: event.touches[0].clientX - position.x,
        y: event.touches[0].clientY - position.y,
      });
    }
  };

  const handleTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
    if (isDragging && zoom > 1 && event.touches.length === 1) {
      setPosition({
        x: event.touches[0].clientX - dragStart.x,
        y: event.touches[0].clientY - dragStart.y,
      });
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const base64Data = file?.includes(",") ? file.split(",")[1] : file;

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    const convertHeic = async () => {
      try {
        if (!file && !url) {
          setConvertedSrc(null);
          setIsConverting(false);
          return;
        }

        const source = url ?? `data:image/heic;base64,${base64Data}`;

        const normalizedSource = source.toLowerCase();

        const isHeic =
          normalizedSource.includes("image/heic") ||
          normalizedSource.includes("image/heif") ||
          normalizedSource.endsWith(".heic") ||
          normalizedSource.endsWith(".heif");

        if (!isHeic) {
          setConvertedSrc(null);
          setIsConverting(false);
          return;
        }

        setIsConverting(true);
        setConvertedSrc(null);

        const response = await fetch(source);

        if (!response.ok) {
          throw new Error(`Failed to load HEIC image: ${response.status}`);
        }

        const blob = await response.blob();

        const convertedBlob = await heicTo({
          blob,
          type: "image/jpeg",
          quality: 0.9,
        });

        if (cancelled) {
          return;
        }

        objectUrl = URL.createObjectURL(convertedBlob);

        setConvertedSrc(objectUrl);
      } catch (err) {
        console.error("HEIC conversion failed:", err);

        if (!cancelled) {
          setConvertedSrc(null);
        }
      } finally {
        if (!cancelled) {
          setIsConverting(false);
        }
      }
    };

    convertHeic();

    return () => {
      cancelled = true;

      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [file, url, base64Data]);

  const renderLoader = () => {
    return (
      <div className="flex min-h-[500px] w-full flex-col items-center justify-center gap-4">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />

        <p className="text-sm text-gray-500 dark:text-gray-400">
          { t("Loading...")}
        </p>
      </div>
    );
  };

  const renderPreview = () => {
    if (isLoading || isConverting) {
      return renderLoader();
    }

    if (type === "IMAGE" && (base64Data || url)) {
      const imageSrc =
        convertedSrc || url || `data:image/png;base64,${base64Data}`;

      return (
        <div className="relative flex flex-col">
          <div
            ref={containerRef}
            className="relative overflow-auto"
            style={{
              width: "100%",
              height: "500px",
              background: "#f0f0f0",
              cursor: zoom > 1 ? (isDragging ? "grabbing" : "grab") : "default",
              position: "relative",
            }}
            onWheel={handleWheelZoom}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img
                ref={imageRef}
                src={imageSrc}
                alt="Preview"
                style={{
                  transform: `
                    scale(${zoom})
                    rotate(${rotation}deg)
                    translate(
                      ${position.x / zoom}px,
                      ${position.y / zoom}px
                    )
                  `,
                  transition: isDragging ? "none" : "transform 0.3s ease",
                  maxWidth: "100%",
                  maxHeight: "100%",
                  transformOrigin: "center center",
                  userSelect: "none",
                  pointerEvents: zoom > 1 ? "none" : "auto",
                }}
                draggable={false}
              />
            </div>

            <div className="absolute bottom-2 left-2 rounded-full bg-black/70 px-3 py-1 text-sm text-white">
              {Math.round(zoom * 100)}%
            </div>
          </div>

          <div className="mt-4 flex flex-wrap justify-center gap-8">
            <button
              onClick={handleZoomIn}
              className="w-fit rounded-full border border-body-text p-2 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
              title={t("Zoom In")}
              type="button"
            >
              <img className="w-6" alt="zoom in" src={zoomInIcon} />
            </button>

            <button
              onClick={handleZoomOut}
              className="w-fit rounded-full border border-body-text p-2 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
              title={t("Zoom Out")}
              type="button"
            >
              <img className="w-6" alt="zoom out" src={zoomOutIcon} />
            </button>

            <button
              onClick={handleRotateLeft}
              className="w-fit rounded-full border border-body-text p-2 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
              title={t("Rotate Left")}
              type="button"
            >
              <img className="w-6" alt="rotate left" src={rotateIcon} />
            </button>

            <button
              onClick={handleRotateRight}
              className="w-fit rounded-full border border-body-text p-2 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
              title={t("Rotate Right")}
              type="button"
            >
              <img
                className="w-6 rotate-180"
                alt="rotate right"
                src={rotateIcon}
              />
            </button>

            <button
              onClick={resetTransforms}
              className="w-fit rounded-full border border-body-text p-2 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
              title={t("Reset")}
              type="button"
            >
              <img className="w-6" alt="reset" src={resetIcon} />
            </button>
          </div>

          <div className="mt-2 text-center text-sm text-gray-500 dark:text-gray-400">
            {t("Drag to pan")} • {Math.round(zoom * 100)}%
          </div>
        </div>
      );
    }

    if (type === "PDF" && base64Data) {
      const byteCharacters = atob(base64Data);

      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);

      const blob = new Blob([byteArray], {
        type: "application/pdf",
      });

      const blobUrl = URL.createObjectURL(blob);

      return (
        <iframe
          src={blobUrl}
          style={{
            width: "100%",
            height: "500px",
          }}
          title="PDF Preview"
        />
      );
    }

    if (type === "PDF" && url) {
      return (
        <iframe
          src={url}
          style={{
            width: "100%",
            height: "500px",
          }}
          title="PDF Preview"
        />
      );
    }

    return (
      <div className="mt-5 flex w-full flex-col items-center justify-center gap-4">
        <img src={secondImage} alt="No Results" />

        <p className="text-center text-body-text">{t("no_file")}</p>
      </div>
    );
  };

  return <div className="w-full">{renderPreview()}</div>;
};

export default FilePreview;


// import React, { useEffect, useState } from "react";
// import secondImage from "../../assets/images/icons/no_results.svg";
// import zoomInIcon from "../../assets/images/icons/zoomIn.svg";
// import zoomOutIcon from "../../assets/images/icons/zoomOut.svg";
// import resetIcon from "../../assets/images/icons/reset.svg";
// import rotateIcon from "../../assets/images/icons/rotate.svg";
// import { useTranslation } from "react-i18next";
// import Loader from "../Common/Loader";
// import heic2any from "heic2any";

// interface IFilePreview {
//   file?: string | null;
//   type?: "IMAGE" | "PDF";
//   isLoading?: boolean;
//   url?: string;
// }

// const FilePreview: React.FC<IFilePreview> = ({
//   file,
//   type,
//   isLoading,
//   url,
// }) => {
//   const [zoom, setZoom] = useState(1);
//   const [rotation, setRotation] = useState(0);
//   const [convertedSrc, setConvertedSrc] = useState<string | null>(null);
//   const [isConverting, setIsConverting] = useState(false);

//   const { t } = useTranslation();

//   const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.2, 3));
//   const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.2, 0.5));
//   const handleRotateLeft = () => setRotation((prev) => prev - 90);
//   const handleRotateRight = () => setRotation((prev) => prev + 90);
//   const resetTransforms = () => {
//     setZoom(1);
//     setRotation(0);
//   };
//   const handleWheelZoom = (event: React.WheelEvent<HTMLDivElement>) => {
//     event.preventDefault();
//     const zoomDelta = event.deltaY > 0 ? -0.1 : 0.1;
//     setZoom((prev) => Math.min(Math.max(prev + zoomDelta, 0.5), 3));
//   };

//   const base64Data = file?.includes(",") ? file?.split(",")[1] : file;

//   useEffect(() => {
//     const convertHeic = async () => {
//       try {
//         if (!file && !url) return;
//         setIsConverting(true);

//         const source = url ?? `data:image/heic;base64,${base64Data}`;

//         const isHeic =
//           source.includes("image/heic") ||
//           source.includes("image/heif") ||
//           source.endsWith(".heic") ||
//           source.endsWith(".heif");

//         if (!isHeic) {
//           setConvertedSrc(null);
//           return;
//         }

//         const res = await fetch(source);
//         const blob = await res.blob();

//         const convertedBlob = (await heic2any({
//           blob,
//           toType: "image/png",
//         })) as Blob;

//         const convertedUrl = URL.createObjectURL(convertedBlob);
//         setConvertedSrc(convertedUrl);
//         setIsConverting(false);
//       } catch (err) {
//         console.error("HEIC conversion failed:", err);
//         setIsConverting(false);
//         setConvertedSrc(null);
//       }
//     };

//     convertHeic();
//   }, [file, url]);

//   const renderPreview = () => {
//     if (type === "IMAGE" && (base64Data || url)) {
//       return (
//         <div className="relative" onWheel={handleWheelZoom}>
//           <div
//             className="flex items-center justify-center overflow-hidden"
//             style={{
//               width: "100%",
//               height: "500px",
//               background: "#f0f0f0",
//             }}
//           >
//             <img
//               src={
//                 convertedSrc
//                   ? convertedSrc
//                   : (url ?? `data:image/png;base64,${base64Data}`)
//               }
//               alt="Preview"
//               style={{
//                 transform: `scale(${zoom}) rotate(${rotation}deg)`,
//                 transition: "transform 0.3s ease",
//                 maxWidth: "100%",
//                 maxHeight: "100%",
//               }}
//             />
//           </div>
//           <div className="mt-4 flex justify-center gap-8">
//             <button
//               onClick={handleZoomIn}
//               className="border-body-text w-fit rounded-full border p-2"
//             >
//               <img className="w-6" alt="zoom" src={zoomInIcon} />
//             </button>
//             <button
//               onClick={handleZoomOut}
//               className="border-body-text w-fit rounded-full border p-2"
//             >
//               <img className="w-6" alt="zoom" src={zoomOutIcon} />
//             </button>
//             <button
//               onClick={handleRotateLeft}
//               className="border-body-text w-fit rounded-full border p-2"
//             >
//               <img className="w-6" alt="rotate" src={rotateIcon} />
//             </button>
//             <button
//               onClick={handleRotateRight}
//               className="border-body-text w-fit rounded-full border p-2"
//             >
//               <img className="w-6 rotate-180" alt="rotate" src={rotateIcon} />
//             </button>
//             <button
//               onClick={resetTransforms}
//               className="border-body-text w-fit rounded-full border p-2"
//             >
//               <img className="w-6" alt="reset" src={resetIcon} />
//             </button>
//           </div>
//         </div>
//       );
//     }

//     if (type === "PDF" && base64Data) {
//       const byteCharacters = atob(base64Data);
//       const byteNumbers = new Array(byteCharacters.length);

//       for (let i = 0; i < byteCharacters.length; i++) {
//         byteNumbers[i] = byteCharacters.charCodeAt(i);
//       }

//       const byteArray = new Uint8Array(byteNumbers);
//       const blob = new Blob([byteArray], { type: "application/pdf" });
//       const blobUrl = URL.createObjectURL(blob);

//       return (
//         <iframe
//           src={blobUrl ?? url}
//           style={{ width: "100%", height: "500px" }}
//           title="PDF Preview"
//         />
//       );
//     }

//     if (isLoading || isConverting) {
//       return (
//         <div className="mt-5 flex min-h-[40vh] w-full flex-col items-center justify-center gap-4">
//           <Loader />
//         </div>
//       );
//     }

//     return (
//       <div className="mt-5 flex w-full flex-col items-center justify-center gap-4">
//         <img src={secondImage} alt="No Results" />
//         <p className="text-body-text text-center">{t("no_file")}</p>
//       </div>
//     );
//   };

//   return <div className="w-full">{renderPreview()}</div>;
// };

// export default FilePreview;
